import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {MapPin, Check, Building2} from 'lucide-react';
import BookingModal from '../components/BookingModal';

const ByCity = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const cities = [
    {
      name: 'Vancouver',
      image: 'https://images.unsplash.com/photo-1559511260-66a654ae982a?w=800',
      description: 'Virtual travel health consults for travellers living in Vancouver, with coordinated vaccine appointments at local pharmacies and clinics.'
    },
    {
      name: 'Surrey',
      image: 'https://images.unsplash.com/photo-1590012314607-cda9d9b699ae?w=800',
      description: 'Expert travel medicine guidance for Surrey residents, with prescriptions sent directly to partner pharmacies near you.'
    },
    {
      name: 'Langley',
      image: 'https://images.unsplash.com/photo-1519659528534-7fd733a832a0?w=800',
      description: 'Comprehensive travel health consultations for Langley travellers, coordinating vaccines with local healthcare partners.'
    },
    {
      name: 'Abbotsford',
      image: 'https://images.unsplash.com/photo-1464207687429-7505649dae38?w=800',
      description: 'Virtual travel consults for Abbotsford residents, with seamless vaccine coordination at nearby clinics and pharmacies.'
    },
    {
      name: 'Chilliwack',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
      description: 'Professional travel health guidance for Chilliwack travellers, with prescriptions and vaccines coordinated locally.'
    }
  ];

  const benefits = [
    'No waiting rooms - consult from home',
    'Evening & weekend appointments',
    'Broad range of travel vaccines',
    'Direct coordination with local pharmacies',
    'Digital vaccination records',
    'Trip-specific health advice'
  ];

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center bg-gradient-to-br from-navy-900 via-navy-800 to-cyan-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" />
        
        <div className="absolute top-20 right-10 opacity-20">
          <MapPin className="w-32 h-32 text-cyan-400" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}>
            
            <span className="inline-block px-4 py-2 bg-cyan-500/20 text-cyan-300 rounded-full text-sm font-semibold mb-6 backdrop-blur-sm border border-cyan-400/30">
              Serving British Columbia
            </span>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
              Travel Health for Your City,<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                Delivered Virtually
              </span>
            </h1>
            
            <p className="max-w-3xl mx-auto text-lg md:text-xl text-gray-200 mb-8 leading-relaxed">
              Expert travel medicine consultations from home, with vaccines and prescriptions coordinated at pharmacies and clinics near you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Cities Grid */}
      <section className="py-20 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-4">
              Select Your City
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Virtual consultations available across BC, with local pharmacy coordination in your area.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cities.map((city, index) => (
              <motion.div
                key={city.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white dark:bg-navy-800 rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10 hover:border-cyan-500/50 transition-all shadow-lg hover:shadow-2xl hover:shadow-cyan-500/10">
                
                {/* City Image */}
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={city.image} 
                    alt={city.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-navy-900/40 to-transparent" />
                  
                  <div className="absolute bottom-4 left-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-cyan-400" />
                    <h3 className="text-2xl font-bold text-white">{city.name}</h3>
                  </div>
                </div>

                {/* City Content */}
                <div className="p-6">
                  <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                    {city.description}
                  </p>
                  
                  <button
                    onClick={() => setIsBookingModalOpen(true)}
                    className="w-full px-6 py-3 bg-gradient-to-r from-coral-500 to-coral-400 hover:from-coral-400 hover:to-coral-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all">
                    Book Virtual Consult – {city.name}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50 dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 rounded-full text-sm font-semibold mb-4">
                Virtual + Local
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-6">
                How We Coordinate with Local Clinics
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed text-lg">
                Haven Travel Health partners with pharmacies and clinics across British Columbia to deliver a seamless travel health experience. After your virtual consultation, we coordinate directly with local healthcare partners to ensure your vaccines and medications are ready when you need them.
              </p>
              
              <div className="space-y-4">
                {[
                  'Virtual consultation from your home',
                  'Prescriptions sent to your chosen pharmacy',
                  'Vaccines coordinated at nearby clinics',
                  'No waiting rooms or travel required',
                  'Evening and weekend appointments available'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-cyan-500 flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={14} className="text-white" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-navy-900 to-cyan-900 rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-400/20 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <Building2 className="w-12 h-12 text-cyan-400 mb-6" />
                <h3 className="text-2xl font-bold mb-6">What You Get</h3>
                
                <div className="space-y-4">
                  {benefits.map((benefit, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg backdrop-blur-sm border border-white/10">
                      <Check className="w-5 h-5 text-cyan-400 shrink-0" />
                      <span className="text-gray-100">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-coral-500 to-coral-400">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Book your virtual travel health consultation today. Available across BC.
          </p>
          <button
            onClick={() => setIsBookingModalOpen(true)}
            className="px-10 py-4 bg-white text-coral-600 text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
            Book Virtual Consult
          </button>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>
  );
};

export default ByCity;